import { useEffect, useState } from 'react';
import {
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  Badge,
  Loader,
  Alert,
  SimpleGrid,
  Paper,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getActualVsExpectedSummary } from '../api/prediction-api';
import type { ActualVsExpectedSummary } from '../types/prediction-types';

export default function ActualVsExpectedPage() {
  const [startDate, setStartDate] = useState<Date>(
    dayjs().subtract(30, 'days').toDate()
  );
  const [endDate, setEndDate] = useState<Date>(dayjs().toDate());
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<ActualVsExpectedSummary[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getActualVsExpectedSummary({
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
      });

      // SAFETY: Ensure response.results is an array
      setSummaryData(Array.isArray(response.results) ? response.results : []);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load data',
        color: 'red',
      });
      setSummaryData([]); // Reset to empty on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Separate revenue and prep time data
  // SAFETY: Filter out null/undefined items and check target_variable exists
  const revenueData = (Array.isArray(summaryData) ? summaryData : [])
    .filter(d => d && d.target_variable === 'revenue');
  const prepTimeData = (Array.isArray(summaryData) ? summaryData : [])
    .filter(d => d && d.target_variable === 'preparation_time');

  // Calculate summary metrics (already aggregated by SQL!)
  const calculateMetrics = (data: ActualVsExpectedSummary[]) => {
    // SAFETY: Check if data exists
    if (!Array.isArray(data) || data.length === 0) return null;

    // Each item in data is one day's aggregated data
    // We don't need slot_count since data is already aggregated by day
    const numDays = data.length;
    
    // SAFETY: Prevent division by zero
    if (numDays === 0) {
      console.warn('No days found in data, cannot calculate metrics');
      return null;
    }
    
    // Calculate weighted average error (weighted by number of items in each day's within_10_pct_count)
    // Since avg_abs_error_pct is already the average for each day, we can just average those
    const avgError = data.reduce((sum, d) => sum + (d.avg_abs_error_pct || 0), 0) / numDays;
    
    // To calculate accuracy properly, we need to know total predictions
    // Since within_10_pct_count is the count of predictions within 10%, 
    // and we have avg_abs_error_pct for each day, we can estimate:
    // For now, we'll show accuracy as percentage of days with at least one good prediction
    const daysWithAccuratePredictions = data.filter(d => (d.within_10_pct_count || 0) > 0).length;
    const accuracyRate = (daysWithAccuratePredictions / numDays) * 100;

    const totalPredicted = data.reduce((sum, d) => sum + (d.total_predicted || 0), 0);
    const totalActual = data.reduce((sum, d) => sum + (d.total_actual || 0), 0);
    const totalError = data.reduce((sum, d) => sum + (d.total_error || 0), 0);

    return {
      avgError: avgError.toFixed(1),
      accuracyRate: accuracyRate.toFixed(1),
      totalPredicted: totalPredicted.toFixed(2),
      totalActual: totalActual.toFixed(2),
      totalError: totalError.toFixed(2),
      sampleSize: numDays,
    };
  };

  const revenueMetrics = calculateMetrics(revenueData);
  const prepTimeMetrics = calculateMetrics(prepTimeData);

  // Prepare chart data (already aggregated!)
  const prepareChartData = (data: ActualVsExpectedSummary[]) => {
    return data.map(item => ({
      date: item.date,
      predicted: parseFloat(item.total_predicted.toFixed(2)),
      actual: parseFloat(item.total_actual.toFixed(2)),
      error: parseFloat(item.total_error.toFixed(2)),
      count: item.slot_count,
    })).sort((a, b) => a.date.localeCompare(b.date));
  };

  const revenueChartData = prepareChartData(revenueData);
  const prepTimeChartData = prepareChartData(prepTimeData);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <Card shadow="md" padding="sm" withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={600}>{dayjs(label).format('MMM D, YYYY')}</Text>
          <Text size="xs" c="dimmed">{data.count} slots</Text>
          <Group gap="xs">
            <Badge size="sm" color="blue">Predicted: {payload[0].value}</Badge>
            <Badge size="sm" color="green">Actual: {payload[1].value}</Badge>
          </Group>
          <Badge size="sm" color={data.error > 0 ? 'green' : 'red'}>
            Error: {data.error > 0 ? '+' : ''}{data.error}
          </Badge>
        </Stack>
      </Card>
    );
  };

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>Actual vs Expected</Title>
        <Text c="dimmed">Compare predicted values against actual results</Text>
      </div>

      {/* Date Range and Actions */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={(date) => date && setStartDate(date)}
              maxDate={endDate}
            />
            <DatePickerInput
              label="End Date"
              value={endDate}
              onChange={(date) => date && setEndDate(date)}
              minDate={startDate}
              maxDate={new Date()}
            />
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={fetchData}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Card>

      {loading ? (
        <Paper shadow="sm" p="xl" withBorder>
          <Group justify="center">
            <Loader />
            <Text>Loading comparison data...</Text>
          </Group>
        </Paper>
      ) : summaryData.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No Data" color="blue">
          No completed games with predictions found for this date range.
        </Alert>
      ) : !revenueMetrics && !prepTimeMetrics ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No Valid Data" color="orange">
          Data was found but contains no valid prediction slots. This may happen if:
          <ul style={{ marginTop: '8px', marginBottom: 0 }}>
            <li>Games have no actual sales data yet</li>
            <li>Predictions have not been matched with actual results</li>
            <li>The data is still being processed</li>
          </ul>
        </Alert>
      ) : (
        <Stack gap="lg">
          {/* Revenue Section */}
          {revenueMetrics && (
            <Stack gap="md">
              <Title order={3}>Revenue Predictions</Title>

              {/* Summary Cards */}
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Avg Error %</Text>
                  <Text size="xl" fw={700}>{revenueMetrics.avgError}%</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Days with Good Predictions</Text>
                  <Text size="xl" fw={700}>{revenueMetrics.accuracyRate}%</Text>
                  <Text size="xs" c="dimmed">(within ±10%)</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Total Predicted</Text>
                  <Text size="xl" fw={700}>${revenueMetrics.totalPredicted}</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Total Actual</Text>
                  <Text size="xl" fw={700} c={parseFloat(revenueMetrics.totalError) > 0 ? 'green' : 'red'}>
                    ${revenueMetrics.totalActual}
                  </Text>
                </Card>
              </SimpleGrid>

              {/* Chart */}
              <Card shadow="sm" padding="lg" withBorder>
                <Title order={4} mb="md">Daily Revenue: Predicted vs Actual</Title>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => dayjs(date).format('MMM D')}
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="predicted" fill="#228be6" name="Predicted" />
                    <Bar dataKey="actual" fill="#40c057" name="Actual" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Stack>
          )}

          {/* Prep Time Section */}
          {prepTimeMetrics && (
            <Stack gap="md">
              <Title order={3}>Preparation Time Predictions</Title>

              {/* Summary Cards */}
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Avg Error %</Text>
                  <Text size="xl" fw={700}>{prepTimeMetrics.avgError}%</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Accuracy (±10%)</Text>
                  <Text size="xl" fw={700}>{prepTimeMetrics.accuracyRate}%</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Total Predicted</Text>
                  <Text size="xl" fw={700}>{prepTimeMetrics.totalPredicted} min</Text>
                </Card>
                <Card shadow="sm" padding="md" withBorder>
                  <Text size="xs" c="dimmed">Total Actual</Text>
                  <Text size="xl" fw={700} c={parseFloat(prepTimeMetrics.totalError) > 0 ? 'green' : 'red'}>
                    {prepTimeMetrics.totalActual} min
                  </Text>
                </Card>
              </SimpleGrid>

              {/* Chart */}
              <Card shadow="sm" padding="lg" withBorder>
                <Title order={4} mb="md">Daily Prep Time: Predicted vs Actual</Title>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={prepTimeChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => dayjs(date).format('MMM D')}
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{ value: 'Prep Time (min)', angle: -90, position: 'insideLeft' }}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="predicted" fill="#fab005" name="Predicted" />
                    <Bar dataKey="actual" fill="#fd7e14" name="Actual" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
